import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { User, MapPin, MapPinArea, Receipt, PaperPlaneTilt, CheckCircle, CircleNotch, ArrowSquareOut } from "@phosphor-icons/react";
import { api, API, formatApiError } from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { DispatchWizard, fmt } from "./DispatchWizard";
import { DispatchSlip, slipUrl } from "./DispatchSlip";

const shell = "p-0 gap-0 overflow-hidden flex flex-col w-full max-w-none sm:max-w-3xl h-[100dvh] sm:h-auto sm:max-h-[92vh] rounded-none sm:rounded-2xl";

const markDelivery = async (x, ev, onChanged) => {
  try {
    await api.post(`/field-items/${x.id}/dispatches/${ev.id}/delivery`, { delivered: !ev.delivered });
    toast.success(ev.delivered ? "Marked as not delivered" : "Marked as reached customer site");
    onChanged();
  } catch (e) { toast.error(formatApiError(e)); }
};

const History = ({ x, onViewSlip, onChanged }) => (
  <div className="space-y-2" data-testid="dispatch-history">
    <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Dispatch History</div>
    {x.dispatches.slice().reverse().map((ev) => (
      <div key={ev.id || ev.at} className="rounded-xl border border-slate-200 bg-white p-3 flex flex-wrap sm:flex-nowrap items-center gap-3">
        <span className="w-9 h-9 rounded-lg bg-emerald-50 text-emerald-600 ring-1 ring-emerald-200 flex items-center justify-center"><CheckCircle size={18} weight="duotone" /></span>
        <div className="min-w-0 flex-1 basis-[calc(100%-3rem)] sm:basis-auto text-xs">
          <div className="font-mono font-bold text-slate-800">{ev.dispatch_no} <span className="font-sans font-normal text-slate-500">· {ev.dispatch_date?.split("-").reverse().join("/")} {ev.dispatch_time}</span></div>
          <div className="text-slate-500 truncate">{ev.vehicle_no} · {ev.driver_name} · {ev.items.map((i) => `${i.label} ×${fmt(i.qty)}`).join(", ")}</div>
          <div className={`mt-0.5 font-semibold ${ev.delivered ? "text-emerald-700" : "text-amber-700"}`} data-testid={`delivery-status-${ev.id}`}>
            {ev.delivered ? `Reached site · ${new Date(ev.delivered_at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}` : "Not yet confirmed at site"}
          </div>
        </div>
        <div className="flex gap-2 w-full sm:w-auto justify-end">
        {ev.id && (
          <Button size="sm" variant={ev.delivered ? "outline" : "default"} className="rounded-full h-8 text-xs" data-testid={`mark-delivered-${ev.id}`} onClick={() => markDelivery(x, ev, onChanged)}>
            {ev.delivered ? "Undo" : <><MapPinArea size={14} className="mr-1" /> Reached</>}
          </Button>
        )}
        {ev.id && <Button size="sm" variant="outline" className="rounded-full h-8 text-xs" data-testid={`view-slip-${ev.id}`} onClick={() => onViewSlip(ev.id)}><Receipt size={14} className="mr-1" /> Slip</Button>}
        </div>
      </div>
    ))}
  </div>
);

export function PartyDialog({ x, onClose, onDispatched, onViewSlip, onChanged }) {
  const pending = (x.dispatch_lines || []).filter((l) => l.pending > 0);
  const [wizard, setWizard] = useState(!(x.dispatches || []).length && pending.length > 0);
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className={shell} data-testid="party-dialog">
        <DialogHeader className="px-5 sm:px-6 py-4 border-b border-slate-200 bg-white text-left">
          <DialogTitle className="flex items-center gap-2 text-lg"><User size={20} weight="duotone" className="text-brand-500" /> {x.customer_name || x.text}</DialogTitle>
          <DialogDescription className="flex items-center gap-1 text-xs">{x.customer_address && <><MapPin size={12} /> {x.customer_address}</>}</DialogDescription>
        </DialogHeader>
        {wizard ? (
          <DispatchWizard x={x} onDone={onDispatched} onCancel={() => ((x.dispatches || []).length ? setWizard(false) : onClose())} />
        ) : (
          <div className="flex-1 overflow-y-auto p-5 sm:p-6 space-y-4 bg-slate-50/50">
            {(x.dispatches || []).length > 0 && <History x={x} onViewSlip={onViewSlip} onChanged={onChanged} />}
            {pending.length > 0 ? (
              <Button data-testid="start-dispatch-btn" className="w-full h-12 rounded-full font-bold bg-brand-500 hover:bg-brand-400 text-slate-950" onClick={() => setWizard(true)}>
                <PaperPlaneTilt size={18} className="mr-1.5" /> Dispatch remaining material ({pending.length} items)
              </Button>
            ) : (
              <div className="text-center py-4 text-emerald-700 font-semibold flex items-center justify-center gap-2" data-testid="fully-dispatched"><CheckCircle size={22} weight="fill" /> All material has been dispatched</div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function SlipDialog({ id, token, onClose }) {
  const [slip, setSlip] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    const req = id ? api.get(`/dispatches/${id}`) : axios.get(`${API}/public/dispatch/${token}`);
    req.then((r) => setSlip(r.data)).catch((e) => setErr(formatApiError(e)));
  }, [id, token]);
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className={`${shell} sm:max-w-4xl`} data-testid="slip-dialog">
        <DialogHeader className="px-5 sm:px-6 py-4 border-b border-slate-200 text-left">
          <DialogTitle className="flex items-center gap-2"><Receipt size={20} weight="duotone" className="text-brand-500" /> Dispatch Slip</DialogTitle>
          <DialogDescription className="text-xs">{slip ? <a href={slipUrl(slip.token)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sky-600 font-semibold" data-testid="slip-public-link">Public verification link <ArrowSquareOut size={12} /></a> : "Loading…"}</DialogDescription>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto p-4 sm:p-6 bg-slate-100">
          {err ? <div className="text-center text-rose-600 text-sm py-10">{err}</div>
            : slip ? <DispatchSlip slip={slip} /> : <div className="py-16 flex justify-center"><CircleNotch size={28} className="animate-spin text-slate-400" /></div>}
        </div>
      </DialogContent>
    </Dialog>
  );
}
